import type {
  ListExternalAccountsQuery,
  ListExternalAccountsQueryVariables,
} from 'hooks/customerApi';
import type { UseQueryResponse } from 'urql';
import type { DefaultParams, Params } from 'wouter';
import { ExternalAccountTable } from 'components/link/ExternalAccountTable';
import { LinkSummary } from 'components/link/LinkSummary';
import { TableLoadError } from 'components/TableLoadError/TableLoadError';
import { useListExternalAccountsQuery } from 'hooks/customerApi';
import { useCustomerApiContext } from 'hooks/useCustomerApiContext';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import { useHasWorkspacePermission } from 'hooks/useHasWorkspacePermission';
import React, { useCallback, useMemo, useState } from 'react';
import { getResultOrError } from 'utils/errors';

import { Permissions } from '@fragment/authorization-utils/permissions';
import { PrimaryButtonV2 } from '@fragment/ui/src/components/Button/PrimaryButtonV2/PrimaryButtonV2';

import { AddCustomAccountModal } from './AddCustomAccountModal';
import { IntegrationSettingsLayout } from './IntegrationSettingsLayout';

export type ComponentProps = UseQueryResponse<
  ListExternalAccountsQuery,
  ListExternalAccountsQueryVariables
>[0] & {
  linkId: string;
  retry: VoidFunction;
  canAddAccount: boolean;
};

export const ExternalAccountsListViewComponent = ({
  linkId,
  data,
  error,
  fetching,
  retry,
  canAddAccount,
}: ComponentProps) => {
  const { isError, result: link } = getResultOrError(data?.link, error);
  const [showAddCustomAccountModal, setShowAddCustomAccountModal] =
    useState(false);

  const shouldRenderAddAccountCTA =
    canAddAccount && data?.link?.__typename === 'CustomLink';

  return (
    <IntegrationSettingsLayout
      summarySection={link && <LinkSummary link={link} />}
      summarySectionActions={
        shouldRenderAddAccountCTA && (
          <PrimaryButtonV2
            data-testid="add-account-button"
            onClick={() => setShowAddCustomAccountModal(true)}
          >
            Add Account
            <span className="text-main-500">+</span>
          </PrimaryButtonV2>
        )
      }
    >
      {showAddCustomAccountModal && (
        <AddCustomAccountModal
          onClose={() => setShowAddCustomAccountModal(false)}
          linkId={linkId}
          refresh={retry}
        />
      )}
      {isError ? (
        <TableLoadError
          entityColumnName="Link"
          {...{ error, retry, fetching }}
        />
      ) : (
        <ExternalAccountTable
          data={link.externalAccounts.nodes}
          linkId={linkId}
        />
      )}
    </IntegrationSettingsLayout>
  );
};

type Props<T extends DefaultParams> = {
  params: Params<T>;
};

export const ExternalAccountsListView = <T extends DefaultParams>({
  params: { linkId },
}: Props<T>) => {
  const { context } = useCustomerApiContext();
  const canAddAccount = useHasWorkspacePermission(Permissions.link.sync);
  const variables = useMemo(
    () => ({
      linkId,
    }),
    [linkId]
  );
  const [response, reExecuteQuery] = useListExternalAccountsQuery({
    context,
    pause: !context.token,
    variables,
  });
  const retry = useCallback(
    () => reExecuteQuery({ requestPolicy: 'network-only' }),
    [reExecuteQuery]
  );
  useDocumentTitle('External Accounts');
  return (
    <ExternalAccountsListViewComponent
      {...response}
      retry={retry}
      linkId={linkId}
      canAddAccount={canAddAccount}
    />
  );
};
